// Durable, FIFO offline queue for live-logging mutations — now incl. offline CREATES.
//
// Edits/deletes/notes/reorders of existing entities have always queued here and replay in
// order on reconnect, with the UI updating optimistically. v2.40 adds offline *creates*
// (add a set / add an exercise) via client TEMP IDs:
//
//   • A create is enqueued with a `resolve` spec: which client temp id(s) the server's
//     response will supply real ids for (e.g. a new set's `id`, a new exercise's
//     `new_exercise_id`/`new_set_id`).
//   • The caller renders immediately using the temp id ("tmp-N", from nextTempId()).
//   • On replay, before each op is sent its path + body are REWRITTEN, swapping any temp id
//     for the real id learned from an earlier create. FIFO guarantees a create is sent
//     before anything that depends on it, so dependents always find their mapping.
//   • If a create permanently fails (4xx/5xx), its temp ids are marked DEAD and every later
//     op that references them is dropped (they could never succeed) rather than wedging.
//
// Sessions are always real (you can't start a workout offline), so only session_exercise
// and set ids are ever temporary — which bounds the reconciliation entirely to this file.

const KEY = "wt_outbox";
const ID_KEY = "wt_outbox_idmap"; // { [tempId]: realId } learned from create replays
const SEQ_KEY = "wt_outbox_seq"; // monotonic temp-id counter (survives reloads)

// localStorage with an in-memory fallback, so the module stays importable under test.
const mem = new Map();
const storage = (() => {
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.getItem(KEY);
      return localStorage;
    }
  } catch {}
  return {
    getItem: (k) => (mem.has(k) ? mem.get(k) : null),
    setItem: (k, v) => mem.set(k, v),
    removeItem: (k) => mem.delete(k),
  };
})();

let queue = loadJson(KEY, []);
let idMap = loadJson(ID_KEY, {}); // tempId -> realId
let dead = new Set(); // tempIds whose create permanently failed → drop dependents
let seq = Number(storage.getItem(SEQ_KEY) || 0);
let sender = null; // (method, path, body) => Promise<any>; throws { status } on failure
let flushing = false;
let lastError = false; // a queued op was dropped because the server rejected it
const listeners = new Set();

function loadJson(key, fallback) {
  try {
    const v = JSON.parse(storage.getItem(key));
    if (key === KEY) return Array.isArray(v) ? v : fallback;
    return v && typeof v === "object" ? v : fallback;
  } catch {
    return fallback;
  }
}
function persist() {
  try {
    storage.setItem(KEY, JSON.stringify(queue));
    storage.setItem(ID_KEY, JSON.stringify(idMap));
  } catch {}
  notify();
}
function notify() {
  for (const fn of [...listeners]) {
    try {
      fn();
    } catch {}
  }
}

/** Wire the function used to actually send a request (api.js's `request`). */
export function configure(sendFn) {
  sender = sendFn;
}
export function onChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
export function pendingCount() {
  return queue.length;
}
export function isFlushing() {
  return flushing;
}
export function hasError() {
  return lastError;
}
export function isOffline() {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}
/**
 * Should a create be routed through the queue instead of sent directly? Yes when offline,
 * and ALSO when ops are already queued — so a create can't race ahead of earlier queued
 * ops it might depend on (and server-side set_index stays ordered).
 */
export function shouldQueue() {
  return isOffline() || queue.length > 0;
}

/** A fresh client temp id ("tmp-N"). Monotonic + persisted so reloads never collide. */
export function nextTempId() {
  seq += 1;
  try {
    storage.setItem(SEQ_KEY, String(seq));
  } catch {}
  return `tmp-${seq}`;
}

/** Drop everything (used when the session ends — discard / finish). */
export function clear() {
  queue = [];
  idMap = {};
  dead = new Set();
  lastError = false;
  persist();
}

/**
 * Enqueue an op. `op.resolve` (optional) is `[{ tempId, from }]`: after a successful send,
 * read the real id from `response[from]` and record tempId → realId for later rewrites.
 * `op.kind` (optional) is a label the UI can ask about — see hasPending().
 */
export function enqueue(op) {
  const entry = { method: op.method, path: op.path, body: op.body };
  if (op.resolve && op.resolve.length) entry.resolve = op.resolve;
  if (op.kind) entry.kind = op.kind;
  queue.push(entry);
  lastError = false;
  persist();
}

/**
 * Is an op of this kind still waiting to be sent? Used for the one queued mutation the user
 * needs told about rather than counted: "complete" — a workout that's over on this device but
 * hasn't reached the server. Everything else is fine as an anonymous "N to sync".
 */
export function hasPending(kind) {
  return queue.some((op) => op.kind === kind);
}

// ── Temp-id rewriting ─────────────────────────────────────────────────────────
const isTemp = (v) => typeof v === "string" && v.startsWith("tmp-");
const mapId = (v) => (isTemp(v) && Object.prototype.hasOwnProperty.call(idMap, v) ? idMap[v] : v);
// Direct operations can follow a create that was queued under a temporary id.
export const realId = mapId;

function rewritePath(path) {
  if (!path.includes("tmp-")) return path;
  return path
    .split("/")
    .map((seg) => (isTemp(seg) ? String(mapId(seg)) : seg))
    .join("/");
}
function rewriteBody(body) {
  // The only id-bearing body is the exercise-reorder `order` array.
  if (body && Array.isArray(body.order)) return { ...body, order: body.order.map(mapId) };
  return body;
}
// temp ids still present in an op after rewriting (their create hasn't mapped them).
function unresolvedTemps(op) {
  const out = [];
  for (const seg of op.path.split("/")) if (isTemp(seg)) out.push(seg);
  if (op.body && Array.isArray(op.body.order)) for (const id of op.body.order) if (isTemp(id)) out.push(id);
  return out.filter((t) => !(t in idMap));
}

export async function flush() {
  if (flushing || !sender || isOffline() || !queue.length) return;
  flushing = true;
  notify();
  try {
    while (queue.length) {
      const op = queue[0];

      // An op that still references a temp id whose create permanently FAILED can never
      // succeed — drop it (and flag the error) instead of wedging the queue behind it.
      const stuck = unresolvedTemps(op);
      if (stuck.some((t) => dead.has(t))) {
        lastError = true;
        queue.shift();
        persist();
        continue;
      }

      const path = rewritePath(op.path);
      const body = rewriteBody(op.body);
      let res;
      try {
        res = await sender(op.method, path, body);
      } catch (e) {
        if (!e || e.status === 0) break; // network/offline: keep the op, retry later
        // Server rejected it (4xx/5xx) — it can never succeed, so drop it rather than wedge
        // the queue, flag the error, and poison any temp ids it was meant to create.
        lastError = true;
        if (op.resolve) for (const r of op.resolve) dead.add(r.tempId);
        queue.shift();
        persist();
        continue;
      }

      // Learn real ids from a create's response so later ops can be rewritten.
      if (op.resolve) {
        for (const r of op.resolve) {
          const real = res ? res[r.from] : null;
          if (real != null) idMap[r.tempId] = real;
          else dead.add(r.tempId); // response lacked the id → dependents can't resolve
        }
      }
      queue.shift();
      persist();
    }
  } finally {
    flushing = false;
    notify();
  }
}

// Browser wiring: replay as soon as we're back online or the tab is refocused.
if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    notify();
    flush();
  });
  window.addEventListener("offline", notify);
}
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") flush();
  });
}
